import { ViewPlugin, Decoration, DecorationSet, ViewUpdate, EditorView } from '@codemirror/view';
import { RangeSetBuilder, Extension } from '@codemirror/state';
import { javascript } from '@codemirror/lang-javascript';

/**
 * Decoration for BlazeMeter script delimiters (${#script, ${/script}, closing })
 * Overrides any syntax highlighting color to render as plain black text.
 */
const delimiterMark = Decoration.mark({ class: 'cm-bzm-delimiter' });

/**
 * Decoration for the argument part inside ${#script 'arg'} — green like a string.
 */
const stringMark = Decoration.mark({ attributes: { style: 'color: #008000' } });

const closePattern = /\$\{\/script\}/g;
const openPattern = /(\$\{#script\s*)((?:'[^']*'|"[^"]*"|[^}]*)?)(\})/g;

function buildDecorations(view: EditorView): DecorationSet {
  const doc = view.state.doc;
  const allRanges: Array<{ from: number; to: number; deco: Decoration }> = [];

  for (const { from, to } of view.visibleRanges) {
    const text = doc.sliceString(from, to);

    closePattern.lastIndex = 0;
    let match;
    while ((match = closePattern.exec(text)) !== null) {
      allRanges.push({
        from: from + match.index,
        to: from + match.index + match[0].length,
        deco: delimiterMark,
      });
    }

    openPattern.lastIndex = 0;
    while ((match = openPattern.exec(text)) !== null) {
      const start = from + match.index;
      const delimEnd = start + match[1].length;
      const argEnd = delimEnd + match[2].length;
      const end = argEnd + match[3].length;

      allRanges.push({ from: start, to: delimEnd, deco: delimiterMark });
      if (match[2].length > 0) {
        allRanges.push({ from: delimEnd, to: argEnd, deco: stringMark });
      }
      allRanges.push({ from: argEnd, to: end, deco: delimiterMark });
    }
  }

  allRanges.sort((a, b) => a.from - b.from || a.to - b.to);

  const builder = new RangeSetBuilder<Decoration>();
  for (const { from, to, deco } of allRanges) {
    builder.add(from, to, deco);
  }
  return builder.finish();
}

const blazemeterDecorationPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = buildDecorations(view);
    }
    update(update: ViewUpdate) {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = buildDecorations(update.view);
      }
    }
  },
  { decorations: v => v.decorations },
);

export function createBlazeMeterExtension(): Extension[] {
  return [javascript(), blazemeterDecorationPlugin];
}
